import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import moment from 'moment';
import { Email } from '@mui/icons-material';
import { Button } from '@src/components/ui/button/button';
import { useBookNow } from './api/book-now';
import toastr from 'toastr';
import { BOOK_NOW_SCHEMA } from './validators';
import { cn } from '@src/utils/ui';

export const transformContactSellerPayload = (values, { itemId }) => {
    const startMoment = moment(values.date).set({
        hour: moment(values.time).hour(),
        minute: moment(values.time).minute(),
        second: moment(values.time).second()
    });

    const formattedAddOns =
        values?.addOns?.map(addOn => ({
            id: addOn.id,
            name: addOn.name,
            description: addOn.description,
            priceChange: parseFloat(addOn.priceChange ?? 0),
            active: addOn.active
        })) || [];

    return {
        itemId,
        quantity: values.quantity,
        bookingSlot: {
            startDateTime: startMoment.unix(),
            endDateTime: 0
        },
        addOns: formattedAddOns
    };
};

export const Actions = ({ itemDetails, user, getUserChannels, getChatDetails, createChatChannel }) => {
    const {
        setError,
        handleSubmit,
        formState: { errors }
    } = useFormContext();
    const showGeneralError = errors.date || errors.time || errors.quantity;
    const [isRequestingAService, setIsRequestingAService] = useState(false); // Legacy
    const bookNowMutation = useBookNow();
    const isLoading = isRequestingAService || bookNowMutation.isPending;

    const isUserNotAllowedToTransact = (user, itemDetails, isLoading) => {
        if (user.Guest) {
            location.href = `/accounts/non-private/sign-in?returnUrl=${location.pathname.slice(1)}`;
            return true;
        }

        if (isLoading) return true;

        if (user.ID === itemDetails.seller.id) {
            toastr.error('Cannot proceed, this item seems to belong to you.', 'Oops! Something went wrong.');
            return true;
        }

        return false;
    };

    const handleMessage = useCallback(values => {
        if (isUserNotAllowedToTransact(user, itemDetails, isLoading)) return;

        const date = values.date || moment();
        const time = values.time || moment().startOf('second');
        const datePart = date.format('YYYY-MM-DD');
        const timePart = time.format('HH:mm:ss');

        if (user.ID === itemDetails.seller.id) {
            toastr.error('Cannot open chat, this item seems to belong to you.', 'Oops! Something went wrong.');
            return;
        }

        setIsRequestingAService(true);

        const options = {
            pageSize: 100,
            pageNumber: 1,
            includes: ['CartItemDetails', 'ItemDetails', 'User']
        };

        getUserChannels(options, channels => {
            const existing = findExistingChannel(channels);
            if (!existing) {
                createOrRedirectToChannel(datePart, timePart, values.addOns || []);
            } else {
                getChatDetails(existing.ChannelID, chatDetails => {
                    if (chatDetails?.Channel?.Offer?.Accepted) {
                        createOrRedirectToChannel(datePart, timePart, values.addOns || []);
                    } else {
                        toastr.error(
                            'You still have an open channel/offer for this item.',
                            'Oops! Something went wrong.'
                        );
                        window.location = '/chat?channelId=' + existing.ChannelID;
                    }
                });
            }
        });
    }, []);

    const handleBookNow = useCallback(
        async values => {
            if (isUserNotAllowedToTransact(user, itemDetails, isLoading)) return;

            try {
                const parsed = BOOK_NOW_SCHEMA.safeParse(values);

                if (!parsed.success) {
                    parsed.error.errors.forEach(err => {
                        setError(err.path[0], { type: 'manual', message: err.message });
                    });
                    return;
                }

                const payload = transformContactSellerPayload(values, { itemId: itemDetails.id });
                const response = await bookNowMutation.mutateAsync(payload);

                if (response.data.success) {
                    window.location.href = `/checkout/express?cartId=${response.data.data.cartId}`;
                } else {
                    toastr.error(response.data.message);
                }
            } catch (error) {
                console.error(error);

                if (error.response?.data?.message) {
                    toastr.error(error.response.data.message);
                } else {
                    toastr.error('Please try again later.', 'Oops! Something went wrong.');
                }
            }
        },
        [bookNowMutation, itemDetails.id]
    );

    const createOrRedirectToChannel = (datePart, timePart, addOns) => {
        const bookingUnix = moment(`${datePart}T${timePart}`).unix();

        createChatChannel(
            {
                recipientId: itemDetails.seller.id,
                itemId: itemDetails.id,
                bookingSlot: {
                    FromDateTime: bookingUnix,
                    ToDateTime: bookingUnix
                },
                createCartItem: true,
                addOns
            },
            handleNewChannelResponse
        );
    };

    const handleNewChannelResponse = newChannel => {
        if (newChannel?.ChannelID) {
            window.location = '/chat?channelId=' + newChannel.ChannelID;
            return;
        }

        toastr.error(newChannel?.message || 'Oops! Something went wrong.');
        setIsRequestingAService(false);
    };

    const findExistingChannel = channels => {
        if (!channels || channels.TotalRecords === 0) return null;

        for (let i = 0; i < channels.TotalRecords; i++) {
            const tempChannel = channels.Records[i];
            if (!tempChannel) continue;

            if (tempChannel.ItemDetails?.id === itemDetails.id) return tempChannel;
            if (tempChannel.CartItemDetails?.ItemDetails?.ID === itemDetails.id) return tempChannel;
        }

        return null;
    };

    return (
        <>
            {itemDetails.instantBuy && (
                <Button
                    type='button'
                    variant='contained'
                    className='tw-w-full tw-h-[42px] tw-text-[12.5px]'
                    onClick={handleSubmit(handleBookNow)}
                    disabled={isLoading}
                >
                    {bookNowMutation.isPending ? 'LOADING...' : 'BOOK'}
                </Button>
            )}
            {showGeneralError && <p className='tw-text-red-600 tw-text-[12px] tw-mt-1'>{showGeneralError.message}</p>}
            <Button
                type='button'
                variant={itemDetails.instantBuy ? 'outlined' : 'contained'}
                className={cn('tw-w-full tw-h-[42px] tw-text-[12.5px]', itemDetails.instantBuy && 'tw-bg-white')}
                startIcon={<Email />}
                onClick={handleSubmit(handleMessage)}
                disabled={isLoading}
            >
                {isRequestingAService ? 'LOADING...' : 'Chat / Request Quote'}
            </Button>
            <p className='tw-text-sm tw-mt-3'>
                Tasker Insurance is included on eligible tasks booked through BCAA Task Marketplace.*
            </p>
        </>
    );
};

Actions.propTypes = {
    user: PropTypes.shape({
        ID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        Guest: PropTypes.bool
    }).isRequired,
    itemDetails: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        seller: PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            instantBuy: PropTypes.bool
        }).isRequired,
        price: PropTypes.number,
        instantBuy: PropTypes.bool
    }).isRequired,
    getUserChannels: PropTypes.func.isRequired,
    getChatDetails: PropTypes.func.isRequired,
    createChatChannel: PropTypes.func.isRequired
};
